import { useEffect, useRef, useState } from "react";
import { useI18n } from "@/lib/i18n";

// ---------- localized copy ---------- //
const SCENES_DE = [
  {
    id: "ankunft",
    over: "Station 01 · Ankunft",
    title: "Ihre Bauteile kommen an.",
    body: "Wareneingang, Sichtprüfung, Etikettierung — jede Charge wird von Hand entgegengenommen.",
    img: "https://images.unsplash.com/photo-1565688534245-05d6b5be184a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800",
  },
  {
    id: "vorbehandlung",
    over: "Station 02 · Vorbehandlung",
    title: "Entfetten. Beizen. Spülen.",
    body: "Bevor der eigentliche Prozess startet, wird jede Oberfläche in mehreren Bädern gereinigt und aktiviert.",
    img: "https://images.unsplash.com/photo-1581093588401-fbb62a02f120?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800",
  },
  {
    id: "galvanik",
    over: "Station 03 · Galvanik",
    title: "Der Kern unserer Arbeit.",
    body: "Verzinken, Zink-Nickel, chemisch Nickel — kontrolliert bis auf den Mikrometer, dokumentiert für jede Charge.",
    img: "https://images.unsplash.com/photo-1618761714954-0b8cd0026356?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800",
  },
  {
    id: "finish",
    over: "Station 04 · Finishing",
    title: "Trocknen. Passivieren. Prüfen.",
    body: "Salzsprühkabine, Schichtdickenmessung, Sichtkontrolle — nichts verlässt das Haus ohne Freigabe.",
    img: "https://images.unsplash.com/photo-1567789884554-0b844b597180?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800",
  },
  {
    id: "versand",
    over: "Station 05 · Versand",
    title: "Zurück zu Ihnen.",
    body: "Verpackt, dokumentiert, tracking-fähig — im Schnitt 5–7 Werktage nach Wareneingang.",
    img: "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800",
  },
];
const SCENES_EN = [
  { over: "Stop 01 · Intake",       title: "Your parts arrive.",         body: "Goods-in, visual check, labelling — every batch is received by hand." },
  { over: "Stop 02 · Pre-treatment", title: "Degrease. Pickle. Rinse.",   body: "Before the process starts, every surface is cleaned and activated in multiple baths." },
  { over: "Stop 03 · Plating",       title: "The heart of our craft.",    body: "Zinc, zinc-nickel, electroless nickel — micron-controlled, documented for every batch." },
  { over: "Stop 04 · Finishing",    title: "Dry. Passivate. Test.",       body: "Salt spray, coating thickness, visual — nothing leaves the plant without sign-off." },
  { over: "Stop 05 · Dispatch",     title: "Back to you.",                 body: "Packed, documented, trackable — 5–7 business days on average from goods-in." },
];
const SCENES_FR = [
  { over: "Étape 01 · Réception",     title: "Vos pièces arrivent.",        body: "Réception, contrôle visuel, étiquetage — chaque lot est reçu à la main." },
  { over: "Étape 02 · Prétraitement",  title: "Dégraisser. Décaper. Rincer.", body: "Avant le procédé, chaque surface est nettoyée et activée dans plusieurs bains." },
  { over: "Étape 03 · Galvano",        title: "Le cœur de notre métier.",     body: "Zingage, zinc-nickel, nickel chimique — contrôlé au micron, documenté par lot." },
  { over: "Étape 04 · Finition",       title: "Sécher. Passiver. Tester.",    body: "Brouillard salin, épaisseur, contrôle visuel — rien ne sort sans validation." },
  { over: "Étape 05 · Expédition",     title: "Retour chez vous.",             body: "Emballé, documenté, traçable — 5 à 7 jours ouvrés en moyenne." },
];
const SCENES_IT = [
  { over: "Tappa 01 · Ricezione",       title: "I Vostri pezzi arrivano.",        body: "Accettazione merci, controllo visivo, etichettatura — ogni lotto è ricevuto a mano." },
  { over: "Tappa 02 · Pretrattamento",   title: "Sgrassare. Decapare. Sciacquare.", body: "Prima del processo, ogni superficie viene pulita e attivata in più bagni." },
  { over: "Tappa 03 · Galvanica",        title: "Il cuore del nostro mestiere.",    body: "Zincatura, zinco-nichel, nichel chimico — controllato al micron, documentato per lotto." },
  { over: "Tappa 04 · Finitura",         title: "Asciugare. Passivare. Testare.",   body: "Nebbia salina, spessore, controllo visivo — nulla esce senza approvazione." },
  { over: "Tappa 05 · Spedizione",       title: "Ritorno a Voi.",                    body: "Imballato, documentato, tracciabile — 5–7 giorni lavorativi in media." },
];

function localizeScenes(lang) {
  const bases = { de: SCENES_DE, en: SCENES_EN, fr: SCENES_FR, it: SCENES_IT }[lang] || SCENES_DE;
  // Non-DE variants reuse the DE image + id via index
  return SCENES_DE.map((base, i) => ({ ...base, ...bases[i] }));
}

const OVERTITLE = {
  de: "— Werkstour",
  en: "— Factory tour",
  fr: "— Visite d'usine",
  it: "— Visita in officina",
};
const HEADLINE = {
  de: { a: "Fünf Stationen.", b: "Ein Weg zur perfekten Oberfläche." },
  en: { a: "Five stops.", b: "One path to the perfect surface." },
  fr: { a: "Cinq étapes.", b: "Un chemin vers la surface parfaite." },
  it: { a: "Cinque tappe.", b: "Un percorso verso la superficie perfetta." },
};

export const WerkstourScroll = () => {
  const { lang } = useI18n();
  const scenes = localizeScenes(lang);

  const containerRef = useRef(null);
  const [progress, setProgress] = useState(0); // 0..1 across pinned range
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onScroll = () => {
      const rect = container.getBoundingClientRect();
      const vh = window.innerHeight;
      // container height is scenes.length * vh + vh (last hold)
      const total = container.offsetHeight - vh;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      const p = total > 0 ? scrolled / total : 0;
      setProgress(p);
      const idx = Math.min(scenes.length - 1, Math.floor(p * scenes.length));
      setActiveIdx(idx);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [scenes.length]);

  // Container is (N+1)*vh tall to allow one full frame of hold at the end.
  const containerH = `${(scenes.length + 1) * 100}vh`;

  return (
    <section
      id="werkstour"
      data-testid="werkstour-section"
      className="relative bg-black text-white"
    >
      <div ref={containerRef} style={{ height: containerH }} className="relative">
        {/* pinned viewport */}
        <div className="sticky top-0 h-screen overflow-hidden">
          {/* Header row */}
          <div className="absolute top-0 inset-x-0 z-30 pointer-events-none">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-8 md:pt-10">
              <div className="overline text-white/80 flex items-center gap-3">
                <span className="inline-flex items-center gap-2 px-2 py-1 border border-white/25 text-[10px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-white pulse-dot" />
                  01 → 05
                </span>
                <span>{OVERTITLE[lang] || OVERTITLE.de}</span>
              </div>
              <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[0.95] mt-4 max-w-3xl">
                {(HEADLINE[lang] || HEADLINE.de).a}{" "}
                <span className="text-white/50">{(HEADLINE[lang] || HEADLINE.de).b}</span>
              </h2>
            </div>
          </div>

          {/* Layered scenes */}
          {scenes.map((s, i) => {
            // opacity envelope: fade in around i, hold, fade out toward i+1
            const p = progress * scenes.length;
            const dist = Math.abs(p - i);
            const opacity = Math.max(0, 1 - dist * 0.9);
            // parallax scale
            const scale = 1.08 - Math.min(0.08, dist * 0.06);
            return (
              <div
                key={s.id}
                className="absolute inset-0 will-change-[opacity,transform] transition-opacity duration-300"
                style={{ opacity }}
                data-testid={`scene-${i}`}
                aria-hidden={i === activeIdx ? "false" : "true"}
              >
                {/* background image */}
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{
                    backgroundImage: `url(${s.img})`,
                    transform: `scale(${scale.toFixed(3)})`,
                  }}
                />
                {/* vignette + gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/20" />
                <div className="absolute inset-0 bg-black/40" />
              </div>
            );
          })}

          {/* Text overlay for active scene */}
          <div className="absolute inset-x-0 bottom-0 z-30 pb-14 md:pb-20 pointer-events-none">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
              {scenes.map((s, i) => (
                <div
                  key={s.id + "-txt"}
                  data-testid={`scene-text-${i}`}
                  className={`absolute inset-x-6 md:inset-x-10 bottom-14 md:bottom-20 transition-all duration-500 ${activeIdx === i ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
                  style={{ maxWidth: 720 }}
                >
                  <div className="overline text-white/70 mb-3">{s.over}</div>
                  <div className="font-display text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[0.95] mb-4">
                    {s.title}
                  </div>
                  <div className="text-white/80 text-base md:text-lg leading-relaxed max-w-xl">
                    {s.body}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Progress rail */}
          <div className="absolute right-6 md:right-10 top-1/2 -translate-y-1/2 z-30 flex flex-col items-center gap-3">
            {scenes.map((s, i) => (
              <div key={s.id + "-dot"} className="flex flex-col items-center gap-2">
                <div className={`w-6 h-6 border ${activeIdx === i ? "border-white bg-white text-black" : "border-white/30 text-white/60"} flex items-center justify-center text-[10px] font-mono transition-all`}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                {i < scenes.length - 1 && (
                  <div className="w-px h-8 bg-white/20 relative overflow-hidden">
                    <div
                      className="absolute inset-x-0 top-0 bg-white transition-all duration-300"
                      style={{ height: `${Math.min(100, Math.max(0, (progress * scenes.length - i) * 100))}%` }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Bottom progress bar */}
          <div className="absolute inset-x-0 bottom-0 z-30 h-1 bg-white/10">
            <div
              className="h-full bg-white transition-transform duration-100"
              style={{ transform: `scaleX(${progress})`, transformOrigin: "left" }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default WerkstourScroll;
